import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineBell,
    HiOutlineSearch,
    HiOutlineChevronDown
} from "react-icons/hi";
import { auth, db } from "../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

const CreatorDashboard = () => {
    const [creatorName, setCreatorName] = useState("Creator");
    const [profileImage, setProfileImage] = useState(null);

    React.useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                // Set from Auth first
                if (user.displayName) setCreatorName(user.displayName);
                if (user.photoURL) setProfileImage(user.photoURL);

                // Fetch from Firestore
                try {
                    const docRef = doc(db, "creators", user.uid);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        if (data.name) setCreatorName(data.name);
                        if (data.profileImage) setProfileImage(data.profileImage);
                    }
                } catch (err) {
                    console.error("Error fetching creator name:", err);
                }
            }
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Sidebar - Keeping White as requested */}
            <Sidebar />

            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex flex-col h-screen p-4">

                {/* Top Header */}
                <header className="flex justify-between items-center mb-4 shrink-0">
                    <div className="flex-1 max-w-xl">
                        <div className="relative">
                            <HiOutlineSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                            <input
                                type="text"
                                placeholder="Search"
                                className="w-full bg-white border-none rounded-full py-3 pl-12 pr-4 text-gray-700 shadow-sm focus:ring-2 focus:ring-blue-100 placeholder-gray-400 font-medium"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-6 ml-4">
                        <button className="relative text-gray-500 hover:text-gray-700">
                            <HiOutlineBell className="text-2xl" />
                            {/* Abstract notification dot */}
                            <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full border-2 border-[#FDFBF7]"></span>
                        </button>
                        <Link to="/creator/public-profile" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                            <span className="text-sm font-bold text-gray-800 hidden md:block">{creatorName}</span>
                            <div className="w-10 h-10 rounded-full bg-[#0044CC] flex items-center justify-center text-white font-bold shadow-md overflow-hidden hover:ring-2 hover:ring-blue-300 transition-all">
                                {profileImage ? (
                                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    creatorName.charAt(0).toUpperCase()
                                )}
                            </div>
                        </Link>
                    </div>
                </header>

                <div className="max-w-7xl mx-auto flex-1 flex flex-col overflow-hidden h-full">
                    <h1 className="text-xl font-bold text-gray-900 mb-2 shrink-0">Welcome back, {creatorName}!</h1>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 overflow-hidden">

                        {/* Left Column (Active & Recent) - Span 4 */}
                        <div className="lg:col-span-4 space-y-6 flex flex-col h-full overflow-hidden">

                            {/* Active Campaigns Card */}
                            <div className="bg-gradient-to-br from-blue-50 to-[#E8F0FE] border border-blue-100/50 rounded-[2rem] p-8 relative overflow-hidden flex flex-col justify-between h-[13rem] shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.02] cursor-pointer group">
                                <div className="flex justify-between items-center z-10">
                                    <h3 className="font-extrabold text-[#0044CC]/80 tracking-wide uppercase text-xs">Active Campaigns</h3>
                                    <div className="bg-white/50 p-1.5 rounded-full backdrop-blur-sm group-hover:bg-white transition-colors">
                                        <HiOutlineChevronDown className="transform -rotate-90 text-[#0044CC] transition-transform" />
                                    </div>
                                </div>
                                <div className="text-7xl font-black text-[#0044CC] tracking-tight z-10 mt-1 drop-shadow-sm">07</div>

                                <div className="flex items-center mt-auto z-10 pl-2">
                                    <div className="flex -space-x-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-10 h-10 rounded-full bg-white border-2 border-white flex items-center justify-center text-sm font-bold shadow-sm overflow-hidden z-[10] ring-2 ring-blue-50/50">
                                                <img src={`https://i.pravatar.cc/150?u=${i + 30}`} alt="avatar shadow" className="w-full h-full object-cover" />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Decorational circle */}
                                <div className="absolute -right-12 -bottom-16 w-48 h-48 bg-gradient-to-tr from-blue-200 to-blue-300 rounded-full opacity-40 blur-2xl group-hover:opacity-60 transition-opacity duration-500"></div>
                                <div className="absolute -left-8 -top-8 w-24 h-24 bg-white rounded-full opacity-60 blur-xl"></div>
                            </div>

                            {/* Recent Activity */}
                            <div className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] flex-1 overflow-hidden flex flex-col min-h-0">
                                <h3 className="font-extrabold text-gray-900 mb-6 shrink-0 tracking-tight">Recent Activity</h3>
                                <div className="space-y-1 overflow-y-auto pr-2 custom-scrollbar">
                                    <ActivityItem
                                        time="8:30 PM"
                                        title="Campaign Accepted"
                                        icon="check"
                                        color="bg-green-50 text-green-600 ring-4 ring-white"
                                    />
                                    <ActivityItem
                                        time="2 days ago"
                                        title="Revisions"
                                        icon="refresh"
                                        color="bg-[#0044CC]/10 text-[#0044CC] ring-4 ring-white"
                                    />
                                    <ActivityItem
                                        time="10 Oct 2024"
                                        title="Campaign Completed"
                                        icon="flag"
                                        color="bg-purple-50 text-purple-600 ring-4 ring-white border border-purple-100"
                                    />
                                    <ActivityItem
                                        time="2 days ago"
                                        title="Revisions"
                                        icon="refresh"
                                        color="bg-[#0044CC]/10 text-[#0044CC] ring-4 ring-white"
                                    />
                                    <ActivityItem
                                        time="8:30 PM"
                                        title="Campaign Accepted"
                                        icon="check"
                                        color="bg-green-50 text-green-600 ring-4 ring-white"
                                    />
                                </div>
                            </div>

                        </div>

                        {/* Right Column (Earnings) - Span 8 */}
                        <div className="lg:col-span-8 bg-white p-6 rounded-[2rem] border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] flex flex-col h-full overflow-hidden">
                            <div className="flex justify-between items-center mb-6 shrink-0 px-2">
                                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">My Earnings</h2>
                                <button className="bg-gray-50 border border-gray-100 text-gray-700 px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-gray-100 transition-colors shadow-sm">
                                    October 2024
                                    <HiOutlineChevronDown className="text-lg" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full pb-2 flex-1 overflow-auto px-2">

                                {/* Total Earnings - Big Yellow Card */}
                                <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FFFBEB] rounded-[2rem] p-10 flex flex-col justify-center relative md:row-span-2 shadow-[0_8px_30px_rgb(251,191,36,0.15)] overflow-hidden group">
                                    <p className="text-xl font-bold text-amber-900/60 mb-2">Total</p>
                                    <h3 className="text-6xl font-black text-amber-950 tracking-tight">₹ 70,656</h3>
                                    <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/40 rounded-full blur-2xl group-hover:bg-white/60 transition-colors duration-500"></div>
                                </div>

                                {/* Monthly Earnings - Pink Card */}
                                <div className="bg-gradient-to-br from-[#FFF0F4] to-[#FFF5F8] rounded-[2rem] p-8 flex flex-col justify-center relative shadow-[0_8px_30px_rgb(244,114,182,0.1)] overflow-hidden">
                                    <p className="text-lg font-bold text-rose-900/60 mb-1">October's</p>
                                    <h3 className="text-4xl font-black text-rose-950 tracking-tight">₹ 6,589</h3>
                                    <div className="absolute -right-4 -bottom-8 w-32 h-32 bg-white/40 rounded-full blur-xl"></div>
                                </div>

                                {/* Last Transaction - Mint Card */}
                                <div className="bg-gradient-to-br from-[#ECFDF5] to-[#F2FBF7] rounded-[2rem] p-8 flex flex-col justify-center relative shadow-[0_8px_30px_rgb(52,211,153,0.1)] overflow-hidden">
                                    <p className="text-lg font-bold text-emerald-900/60 mb-1">Last Transaction</p>
                                    <h3 className="text-4xl font-black text-emerald-950 tracking-tight">₹ 2,500</h3>
                                    <div className="absolute -left-4 -bottom-8 w-32 h-32 bg-white/40 rounded-full blur-xl"></div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </main>
        </div>
    );
};

const Sidebar = () => {
    const location = useLocation();

    // Helper to determine active state
    const isActive = (path) => location.pathname === path;

    return (
        <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex shrink-0 z-20">
            <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                <span>Braccet</span>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-2">
                <SidebarItem icon={<HiOutlineHome />} label="Dashboard" to="/creator/dashboard" active={isActive('/creator/dashboard')} />
                <SidebarItem icon={<HiOutlineBriefcase />} label="Campaigns" to="/creator/find-work" />
                <SidebarItem icon={<span className="font-bold text-lg">(B)</span>} label="Brands" to="/creator/brands" />
                <SidebarItem icon={<HiOutlineChatAlt2 />} label="Messages" to="/creator/messages" />
                <SidebarItem icon={<HiOutlineCog />} label="Settings" to="/creator/profile" />
            </nav>

            <div className="p-4 border-t border-gray-100">
                <button className="flex w-full items-center gap-3 p-2 rounded-xl text-gray-500 font-bold hover:bg-gray-50 hover:text-red-500 transition-colors">
                    <svg className="w-6 h-6 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Logout
                </button>
            </div>
        </aside>
    );
};

const SidebarItem = ({ icon, label, to, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

const ActivityItem = ({ time, title, icon, color }) => (
    <div className="flex gap-4 items-start relative pb-8 border-l-2 border-dashed border-gray-100 last:border-0 last:pb-0 ml-4 pl-8 group hover:-translate-y-0.5 transition-transform duration-200 cursor-pointer">
        {/* Icon Dot */}
        <div className={`absolute -left-[1.35rem] top-0 w-10 h-10 rounded-full flex items-center justify-center ${color} shadow-sm z-10 transition-transform group-hover:scale-110`}>
            {/* Simple icon emulation */}
            {icon === 'check' && <i className="fa-solid fa-check text-sm"></i>}
            {icon === 'refresh' && <i className="fa-solid fa-rotate text-sm"></i>}
            {icon === 'flag' && <i className="fa-solid fa-flag text-sm"></i>}
        </div>

        <div className="pt-0.5">
            <h4 className="font-bold text-gray-900 text-[15px] leading-tight mb-1 group-hover:text-[#0044CC] transition-colors">{title}</h4>
            <p className="text-xs text-gray-500 font-medium">{time}</p>
        </div>
    </div>
);

export default CreatorDashboard;
